import type { ViewCamera } from '../types';
import { toCameraBasis } from './projection';

/**
 * A 4x4 view-projection matrix, column-major, as WebGPU wants it in a uniform buffer.
 *
 * Sixteen floats rather than a nested array: this is written straight into a mapped buffer, and
 * the layout has to match what the shader declares.
 */
export type ViewProjectionMatrix = Float32Array;

/** Floor on the homogeneous divisor, matching the software projector's. */
export const MINIMUM_DIVISOR = 0.1;

/**
 * The same transform {@link createProjector} applies, expressed as a matrix.
 *
 * Derived from the identical camera basis rather than rebuilt, so a hardware renderer and the
 * software one cannot drift apart. The two are asserted equal in a test, which is the only way to
 * gain confidence in this on a machine with no GPU: if the matrix reproduces the already-verified
 * projector to floating point, the geometry reaching a shader is right even though the shader
 * itself cannot be run here.
 *
 * Clip space is WebGPU's: x and y in [-1, 1] with y upward, and z in [0, 1].
 */
export const toViewProjectionMatrix = (
  camera: ViewCamera,
  viewport: { width: number; height: number }
): ViewProjectionMatrix => {
  const { right, up, forward } = toCameraBasis(camera);

  /* One scale for both axes, governed by the smaller viewport dimension, then divided back out
     per axis to reach normalised device coordinates. This is what keeps the plot unsheared by the
     aspect ratio - the same rule the software projector follows. */
  const pixelsPerUnit =
    (Math.min(viewport.width, viewport.height) / 2) * camera.zoom;
  const scaleX = pixelsPerUnit / (viewport.width / 2);
  const scaleY = pixelsPerUnit / (viewport.height / 2);
  const { perspective, heightScale } = camera;

  /* The z axis is pre-scaled here rather than by the caller, so vertex buffers can hold the
     field's own normalised height and `heightScale` stays a camera property. */
  const column = (
    axisX: number,
    axisY: number,
    axisZ: number
  ): [number, number, number] => [axisX, axisY, axisZ * heightScale];

  const [rx, ry, rz] = column(right[0], right[1], right[2]);
  const [ux, uy, uz] = column(up[0], up[1], up[2]);
  const [fx, fy, fz] = column(forward[0], forward[1], forward[2]);
  const [tx, ty, tz] = camera.target;

  /* Row 3 is the projective part: w = 1 - k·(forward·p). Dividing by it yields exactly
     1 / (1 - k·depth), which is the software projector's foreshortening. */
  return new Float32Array([
    // column 0
    rx * scaleX,
    ux * scaleY,
    0,
    -perspective * fx,
    // column 1
    ry * scaleX,
    uy * scaleY,
    0,
    -perspective * fy,
    // column 2
    rz * scaleX,
    uz * scaleY,
    0,
    -perspective * fz,
    /* Column 3: the pivot, as `M . T(-target)`.

       Each entry is the row above dotted with the target and negated, which is what turns
       `M . p` into `M . (p - target)` without a second matrix multiply. The basis vectors used
       here are the z-scaled ones, so the translation is scaled the same way the rotation is and
       `heightScale` keeps meaning one thing. */
    -(rx * tx + ry * ty + rz * tz) * scaleX,
    -(ux * tx + uy * ty + uz * tz) * scaleY,
    /* Depth is written by the shader from the view axis rather than carried here, so this stays
       0 and the matrix describes position alone. */
    0,
    /* The projective row, which is `1 - k.(forward . p)` - so a pivot adds back what the forward
       column takes off the target. */
    1 + perspective * (fx * tx + fy * ty + fz * tz),
  ]);
};

export type ClipPoint = { x: number; y: number; w: number };

/**
 * Applies the matrix on the CPU, for the agreement test and for hit-testing without a GPU.
 *
 * Column-major, so element `[column * 4 + row]`.
 */
export const applyViewProjection = (
  matrix: ViewProjectionMatrix,
  x: number,
  y: number,
  z: number
): ClipPoint => {
  const clipX = matrix[0] * x + matrix[4] * y + matrix[8] * z + matrix[12];
  const clipY = matrix[1] * x + matrix[5] * y + matrix[9] * z + matrix[13];
  const clipW = matrix[3] * x + matrix[7] * y + matrix[11] * z + matrix[15];

  return { x: clipX, y: clipY, w: Math.max(MINIMUM_DIVISOR, clipW) };
};

/**
 * Clip space to pixels, so a matrix result can be compared against the software projector.
 *
 * Screen y is flipped: clip y grows upward and pixels grow downward.
 */
export const clipToScreen = (
  { x, y, w }: ClipPoint,
  viewport: { width: number; height: number }
): { screenX: number; screenY: number } => ({
  screenX: ((x / w) * 0.5 + 0.5) * viewport.width,
  screenY: (0.5 - (y / w) * 0.5) * viewport.height,
});
